"use client"

import { IconButton, Image, VideoHandle, Text } from "@/elements"
import React, { FC, useCallback, useEffect, useRef, useState } from "react"
import { FragmentType, graphql, useFragment } from "@/gql"
import { Video } from "@/components"
import { XMarkIcon } from "@heroicons/react/24/outline"
import { ChevronLeftIcon, ChevronRightIcon } from "@/icons/outline"
import { createPortal } from "react-dom"
import useEmblaCarousel from "embla-carousel-react"

const ProjectLightboxFragment = graphql(`
  fragment ProjectLightbox on CreativeWorkPortfolioProject {
    __typename
    id

    files {
      edges {
        node {
          id
          thumbnailUrl645w: thumbnailUrl(width: 645)
          thumbnailUrl2000w: thumbnailUrl(width: 2000)
          metadata {
            video
          }
          ...Video
        }
      }
    }
  }
`)

interface Props {
  project: FragmentType<typeof ProjectLightboxFragment> | undefined | null
  onClose: () => void
  visibleIndex: number
  onUpdateVisibleIndex: (index: number) => void
}

export const ProjectLightbox: FC<Props> = ({ onClose, visibleIndex, onUpdateVisibleIndex, ...props }) => {
  const project = useFragment(ProjectLightboxFragment, props.project)
  const videoRef = useRef<(VideoHandle | null)[]>([])
  const mediaRef = useRef<(HTMLDivElement | null)[]>([])
  const [portalElement, setPortalElement] = useState<HTMLElement | null>(null)
  const [canScrollNext, setCanScrollNext] = useState(true)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [hasSetInitialIndex, setHasSetInitialIndex] = useState(false)

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "center",
    inViewThreshold: 0.6,
    active: !!project?.files?.edges && project?.files?.edges.length > 1,
  })

  const pauseAllVideos = useCallback(() => {
    videoRef.current.forEach(video => {
      video?.pause()
    })
  }, [])

  useEffect(() => {
    // Set initial visible index
    if (emblaApi && !hasSetInitialIndex) {
      emblaApi.scrollTo(visibleIndex, true)
      setHasSetInitialIndex(true) // Ensure it only runs once
    }
  }, [emblaApi, visibleIndex, hasSetInitialIndex])

  const logSlidesInView = useCallback(
    (emblaApi: any) => {
      const inView = emblaApi.slidesInView()
      pauseAllVideos()
      const index = inView[0]
      if (typeof index === "number") {
        onUpdateVisibleIndex(index)
      }
    },
    [pauseAllVideos]
  )

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext()
  }, [emblaApi])

  useEffect(() => {
    if (emblaApi) emblaApi.on("slidesInView", logSlidesInView)
  }, [emblaApi, logSlidesInView])

  useEffect(() => {
    // Dynamically create a portal element if it doesn't exist
    let portalDiv = document.getElementById("lightbox-portal")
    if (!portalDiv) {
      portalDiv = document.createElement("div")
      portalDiv.setAttribute("id", "lightbox-portal")
      document.body.appendChild(portalDiv)
    }
    setPortalElement(portalDiv)

    portalDiv.style.position = "relative"
    portalDiv.style.height = "100vh"
    portalDiv.style.width = "100vw"

    return () => {
      portalDiv.style.position = ""
      portalDiv.style.height = ""
      portalDiv.style.width = ""
      if (portalDiv && !document.getElementById("lightbox-portal")) {
        portalDiv.parentNode?.removeChild(portalDiv)
      }
    }
  }, [])

  const updateScrollButtons = useCallback(() => {
    const hasNext = emblaApi?.canScrollNext() || false
    const hasPrev = emblaApi?.canScrollPrev() || false

    setCanScrollNext(hasNext)
    setCanScrollPrev(hasPrev)
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    updateScrollButtons()
    emblaApi.on("select", updateScrollButtons)
    emblaApi.on("reInit", updateScrollButtons)
  }, [emblaApi, updateScrollButtons])

  const handleCloseLightBox = useCallback(() => {
    pauseAllVideos()
    onClose()
  }, [pauseAllVideos, onClose])

  const onKeydown = useCallback(
    (event: KeyboardEvent) => {
      switch (event.key) {
        case "ArrowRight":
          scrollNext()
          break
        case "ArrowLeft":
          scrollPrev()
          break
        case "Escape":
          onClose()
          break
        default:
          break
      }
    },
    [scrollNext, scrollPrev, onClose]
  )

  useEffect(() => {
    window.addEventListener("keydown", onKeydown)

    return () => {
      window.removeEventListener("keydown", onKeydown)
    }
  }, [onKeydown])

  if (!portalElement) {
    return null
  }

  return createPortal(
    <div className="fixed bottom-0 left-0 right-0 top-0 h-screen w-screen bg-bg-1" style={{ zIndex: 1000 }}>
      <div className="relative h-screen w-screen">
        <div className="h-full w-full overflow-hidden" ref={emblaRef}>
          <div className="flex h-full w-full">
            {project?.files?.edges.map((edge, index) => {
              const node = edge?.node
              if (!node) return null
              return (
                <div
                  className="embla__slide relative flex h-screen w-screen cursor-pointer items-center justify-center"
                  key={index}
                  ref={el => {
                    mediaRef.current[index] = el
                  }}>
                  <div
                    className="relative flex items-center justify-center"
                    style={{
                      height: "calc(100vh - 160px)",
                      width: "calc(100vh - 160px)",
                    }}>
                    {node?.metadata?.video && node ? (
                      <Video
                        video={node}
                        ref={el => {
                          videoRef.current[index] = el
                        }}
                        poster={node?.thumbnailUrl645w ?? ""}
                      />
                    ) : (
                      <Image
                        fetchPriority={index < 5 ? "high" : "low"}
                        srcSetUrls={{
                          sm: node?.thumbnailUrl645w ?? "",
                          md: node?.thumbnailUrl2000w ?? "",
                          lg: node?.thumbnailUrl2000w ?? "",
                          xl: node?.thumbnailUrl2000w ?? "",
                        }}
                        alt="Project image"
                        className="h-full w-full object-contain"
                        onContextMenu={e => e.preventDefault()}
                      />
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
        <div className="absolute right-8 top-8">
          <IconButton onClick={handleCloseLightBox} Icon={XMarkIcon} variant="white" />
        </div>

        {canScrollNext && (
          <div className="absolute right-8 top-1/2 -translate-y-1/2 transform">
            <IconButton Icon={ChevronRightIcon} variant="outline" onClick={scrollNext} />
          </div>
        )}

        {canScrollPrev && (
          <div className="absolute left-8 top-1/2 -translate-y-1/2 transform">
            <IconButton Icon={ChevronLeftIcon} variant="outline" onClick={scrollPrev} />
          </div>
        )}

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 transform">
          <Text className="text-fg-2" variant="body2">
            {visibleIndex + 1} / {project?.files?.edges.length}
          </Text>
        </div>
      </div>
    </div>,
    portalElement!
  )
}
